/**
 * Pure zoom/fit math for the WebGL charts. All y math mirrors the line's
 * baked normalization: n(v) = ((v - yMinP) / yRangeP) * 2 - 1, with a 5%
 * padded full-data range. webgl-plot renders screenY = n * scaleY + offsetY,
 * visible s in [-1, 1]. Keeping every consumer on these formulas keeps the
 * axis, the line, and the tooltip in sync.
 */

export function paddedFullRange(fullYMin: number, fullYMax: number): { yMinP: number; yRangeP: number } {
  const yRange = fullYMax - fullYMin || 1;
  const yPad = yRange * 0.05;
  const yMinP = fullYMin - yPad;
  const yMaxP = fullYMax + yPad;
  return { yMinP, yRangeP: yMaxP - yMinP };
}

/** normalize a data value into the line's baked [-1, 1] space (pre-zoom). */
function norm(v: number, yMinP: number, yRangeP: number): number {
  return ((v - yMinP) / yRangeP) * 2 - 1;
}

/** Visible x-window derived from zoomState, matching AxisOverlay/notifySync. */
export function visibleXRange(
  xMin: number,
  xMax: number,
  scaleX: number,
  offsetX: number,
): { xMin: number; xMax: number } {
  const xRange = xMax - xMin;
  const visibleWidth = xRange / scaleX;
  const centerOffset = offsetX / 2;
  const visibleCenter = xMin + xRange * 0.5 - (centerOffset * xRange) / scaleX;
  return { xMin: visibleCenter - visibleWidth / 2, xMax: visibleCenter + visibleWidth / 2 };
}

/** Min/max of y over points whose timestamp is within [xMin, xMax]. Null if none. */
export function visibleYRange(
  timestamps: number[],
  values: (number | null)[],
  xMin: number,
  xMax: number,
): { yMin: number; yMax: number } | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < timestamps.length; i++) {
    const t = timestamps[i];
    if (t < xMin || t > xMax) continue;
    const v = values[i];
    if (v == null || !Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (lo === Infinity) return null;
  return { yMin: lo, yMax: hi };
}

/**
 * scaleY/offsetY that make the (5%-padded) visible y-window fill s in [-1, 1],
 * relative to the line's baked full-range normalization.
 */
export function fitScaleOffsetY(
  visYMin: number,
  visYMax: number,
  fullYMin: number,
  fullYMax: number,
): { scaleY: number; offsetY: number } {
  const { yMinP, yRangeP } = paddedFullRange(fullYMin, fullYMax);
  const span = visYMax - visYMin;
  const pad = span > 0 ? span * 0.05 : Math.max(Math.abs(visYMin) * 0.05, 1);
  const vMinP = visYMin - pad;
  const vMaxP = visYMax + pad;
  const nLo = norm(vMinP, yMinP, yRangeP);
  const nHi = norm(vMaxP, yMinP, yRangeP);
  const denom = nHi - nLo || 1;
  const scaleY = 2 / denom;
  const offsetY = -1 - nLo * scaleY;
  return { scaleY, offsetY };
}

/** Inverse: the data y-values at the visible bottom (s=-1) and top (s=+1). */
export function axisYExtent(
  fullYMin: number,
  fullYMax: number,
  scaleY: number,
  offsetY: number,
): { yBottom: number; yTop: number } {
  const { yMinP, yRangeP } = paddedFullRange(fullYMin, fullYMax);
  const nBottom = (-1 - offsetY) / scaleY;
  const nTop = (1 - offsetY) / scaleY;
  const toVal = (n: number) => yMinP + ((n + 1) / 2) * yRangeP;
  return { yBottom: toVal(nBottom), yTop: toVal(nTop) };
}
